import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../utils/authContext";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Search, Plus, Edit, Trash2, Database, Settings, HelpCircle } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";
import Alert from "@mui/material/Alert";
import { useAdminSessionState } from './useAdminSessionState';

const AdminReferenceDataEditor = () => {
  const { currentUser } = useAuth();
  const [dataTypes, setDataTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${currentUser?.uid}:referenceSearch`, '');
  const [selectedTypeId, setSelectedTypeId] = useAdminSessionState(`admin:${currentUser?.uid}:referenceType`, null);
  const selectedType = dataTypes.find(type => type.id === selectedTypeId);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Dialog states
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  useEffect(() => {
    fetchDataTypes();
  }, []);

  const fetchDataTypes = async () => {
    try {
      const snapshot = await getDocs(collection(db, "referenceData"));
      const types = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          // Get item count from subcollection
          const itemsSnapshot = await getDocs(collection(db, "referenceData", docSnap.id, "items"));
          return {
            id: docSnap.id,
            ...data,
            itemCount: itemsSnapshot.size
          };
        })
      );
      setDataTypes(types);
    } catch (error) {
      console.error("Error fetching reference data types:", error);
    }
    setIsLoading(false);
  };

  const handleNewType = () => {
    setEditingType(null);
    setTypeDialogOpen(true);
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setTypeDialogOpen(true);
  };

  const handleDeleteType = (type) => {
    setTypeToDelete(type);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;
    try {
      // Delete all items in subcollection first
      const itemsSnapshot = await getDocs(collection(db, "referenceData", typeToDelete.id, "items"));
      await Promise.all(itemsSnapshot.docs.map(itemDoc => deleteDoc(itemDoc.ref)));
      // Delete the type document
      await deleteDoc(doc(db, "referenceData", typeToDelete.id));
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
      fetchDataTypes();
    } catch (error) {
      console.error("Error deleting reference data type:", error);
    }
  };

  const handleSelectType = (type) => {
    setSelectedTypeId(type.id);
  };

  const handleBackToTypes = () => {
    setSelectedTypeId(null);
    fetchDataTypes(); // Refresh counts
  };

  const filteredTypes = dataTypes.filter(type => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      type.name?.toLowerCase().includes(query) ||
      type.key?.toLowerCase().includes(query)
    );
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  // If a type is selected, show the items editor
  if (selectedType) {
    return (
      <AdminDataItems 
        key={selectedType.id}
        dataType={selectedType} 
        onBack={handleBackToTypes}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div style={{ marginBottom: '60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
          <Tooltip title="Search data types" arrow>
            <Search className="header-search-icon" size={18} />
          </Tooltip>
          <input
            type="text"
            placeholder="Search Data Types"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header-search-input"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Help" arrow>
            <IconButton onClick={() => setHelpDialogOpen(true)} sx={{ mr: 1 }}>
              <HelpCircle size={20} />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleNewType}
            variant="contained"
            color="primary"
          >
            Add Data Type
          </Button>
        </div>
      </div>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  {isLoading ? (
                    "Loading..."
                  ) : searchQuery ? (
                    "No data types match your search"
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Database size={48} color="var(--color-text-secondary)" />
                      <span>No reference data types found. Add your first data type to get started.</span>
                      <Button
                        startIcon={<Plus size={16} />}
                        onClick={handleNewType}
                        variant="outlined"
                        size="small"
                      >
                        Add First Data Type
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredTypes.map((type) => (
                <TableRow 
                  key={type.id}
                  sx={{ 
                    '&:hover': { backgroundColor: 'var(--color-background-alt)' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSelectType(type)}
                >
                  <TableCell>
                    <span className="table-primary-text">
                      {type.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small" style={{ fontFamily: 'monospace' }}>
                      {type.key}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <span className="patient-details-small">
                      {type.itemCount || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small">
                      {formatDate(type.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit items" arrow>
                      <IconButton
                        onClick={() => handleSelectType(type)}
                        size="small"
                        color="primary"
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Type settings" arrow>
                      <IconButton
                        onClick={() => handleEditType(type)}
                        size="small"
                      >
                        <Settings size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete type" arrow>
                      <IconButton
                        onClick={() => handleDeleteType(type)}
                        size="small"
                        color="error"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Type Create/Edit Dialog */}
      <ModalPortal isOpen={typeDialogOpen}>
        <DataTypeDialog
          open={typeDialogOpen}
          onClose={() => setTypeDialogOpen(false)}
          editingType={editingType}
          currentUser={currentUser}
          onSave={() => {
            setTypeDialogOpen(false);
            fetchDataTypes();
          }}
        />
      </ModalPortal>

      {/* Delete Confirmation Dialog */}
      <ModalPortal isOpen={deleteDialogOpen}>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
        <DialogTitle>Delete Data Type</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{typeToDelete?.name}"? This will also delete all {typeToDelete?.itemCount || 0} items. This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </ModalPortal>

      {/* Help Dialog */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)} 
        maxWidth="md"
        sx={{
          '& .MuiDialog-container': {
            marginLeft: 'var(--sidebar-width, 200px)',
            width: 'calc(100% - var(--sidebar-width, 200px))'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Database size={24} />
            About Reference Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <strong>What is Reference Data?</strong>
              <p style={{ margin: '8px 0 0 0', color: 'var(--color-text-secondary)' }}>
                Reference data sets (medications, measures, etc.) are lists that get automatically 
                included in AI prompts during document processing. They help the AI make accurate 
                decisions based on our specific terminology and standards.
              </p>
            </Box>

            <Box>
              <strong>How it works:</strong>
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: 'var(--color-text-secondary)' }}>
                <li>Create a <strong>Data Type</strong> (e.g., "Medications")</li>
                <li>Define the <strong>Schema</strong> — what fields each item has</li>
                <li>Write a <strong>Prompt Instruction</strong> — tells the AI how to use this data</li>
                <li>Add <strong>Items</strong> — the actual data entries</li>
              </ol>
            </Box>

            <Box>
              <strong>Column explanations:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: 'var(--color-text-secondary)' }}>
                <li><strong>Name</strong> — Display name for the data type</li>
                <li><strong>Key</strong> — Unique identifier used in prompts (e.g., "medications")</li>
                <li><strong>Items</strong> — Number of data entries in this set</li>
              </ul>
            </Box>

            <Alert severity="info">
              <strong>Example:</strong> A "Medications" data type with items like "alprazolam (Xanax)" 
              helps the AI convert brand names to generic names consistently in transcripts.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Dialog for creating/editing data types
const DataTypeDialog = ({ open, onClose, editingType, currentUser, onSave }) => {
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [promptInstruction, setPromptInstruction] = useState("");
  const [schemaText, setSchemaText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editingType) {
      setName(editingType.name || "");
      setKey(editingType.key || "");
      setPromptInstruction(editingType.promptInstruction || "");
      setSchemaText(JSON.stringify(editingType.schema || {}, null, 2));
    } else {
      setName("");
      setKey("");
      setPromptInstruction("");
      setSchemaText('{\n  "field_name": { "type": "string", "required": true }\n}');
    }
    setError("");
  }, [editingType, open]);

  const handleSave = async () => {
    setError("");
    
    if (!name.trim() || !key.trim()) {
      setError("Name and key are required");
      return;
    }

    let schema;
    try {
      schema = JSON.parse(schemaText);
    } catch (e) {
      setError("Invalid JSON in schema");
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        key: key.trim().toLowerCase(),
        promptInstruction: promptInstruction.trim(),
        schema,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };

      if (editingType) {
        await updateDoc(doc(db, "referenceData", editingType.id), data);
      } else {
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, "referenceData"), data);
      }
      onSave();
    } catch (error) {
      console.error("Error saving data type:", error);
      setError("Failed to save");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>{editingType ? "Edit Data Type" : "Add Data Type"}</DialogTitle>
      <DialogContent>
        {editingType && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changing the schema may affect existing items. The key cannot be changed after creation.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            placeholder="e.g., Medications"
          />
          <TextField
            label="Key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            fullWidth
            placeholder="e.g., medications"
            helperText="Unique identifier used in code references"
            disabled={!!editingType}
          />
          <TextField
            label="Prompt Instruction"
            value={promptInstruction}
            onChange={(e) => setPromptInstruction(e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="Instructions for the AI on how to use this data..."
          />
          <TextField
            label="Schema (JSON)"
            value={schemaText}
            onChange={(e) => setSchemaText(e.target.value)}
            fullWidth
            multiline
            rows={6}
            helperText="Define fields with type (string, number, boolean, string[]) and required flag"
            sx={{ fontFamily: 'monospace' }}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Items editor component
const AdminDataItems = ({ dataType, onBack, currentUser }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${currentUser?.uid}:reference:${dataType.id}:search`, '');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [dataType.id]);

  const fetchItems = async () => {
    try {
      const snapshot = await getDocs(collection(db, "referenceData", dataType.id, "items"));
      const itemsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setItems(itemsData);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
    setIsLoading(false);
  };

  const handleNewItem = () => {
    setEditingItem(null);
    setItemDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemDialogOpen(true);
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete this item?`)) return;
    try {
      await deleteDoc(doc(db, "referenceData", dataType.id, "items", item.id));
      fetchItems();
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const schemaFields = Object.keys(dataType.schema || {});
  
  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return schemaFields.some(field => 
      String(item[field] || "").toLowerCase().includes(query)
    );
  });

  return (
    <div style={{ marginBottom: '60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Button onClick={onBack} variant="text" size="small">
          ← Back
        </Button>
        <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>{dataType.name}</span>
        <span style={{ color: 'var(--color-text-secondary)' }}>({items.length} items)</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
            <Tooltip title="Search items" arrow>
              <Search className="header-search-icon" size={18} />
            </Tooltip>
            <input
              type="text"
              placeholder="Search Items"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="header-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outlined"
          >
            Import JSON
          </Button>
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleNewItem}
            variant="contained"
            color="primary"
          >
            Add Item
          </Button>
        </div>
      </div>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              {schemaFields.map(field => (
                <TableCell key={field} sx={{ fontWeight: 600 }}>
                  {field}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={schemaFields.length + 1} align="center" sx={{ py: 4 }}>
                  {isLoading ? "Loading..." : searchQuery ? "No items match your search" : "No items yet"}
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow 
                  key={item.id}
                  sx={{ '&:hover': { backgroundColor: 'var(--color-background-alt)' } }}
                >
                  {schemaFields.map(field => (
                    <TableCell key={field}>
                      {Array.isArray(item[field]) ? item[field].join(", ") : String(item[field] || "")}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Tooltip title="Edit" arrow>
                      <IconButton onClick={() => handleEditItem(item)} size="small" color="primary">
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton onClick={() => handleDeleteItem(item)} size="small" color="error">
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Item Create/Edit Dialog */}
      <ModalPortal isOpen={itemDialogOpen}>
        <ItemDialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          dataType={dataType}
          editingItem={editingItem}
          onSave={() => {
            setItemDialogOpen(false);
            fetchItems();
          }}
        />
      </ModalPortal>

      {/* Import Dialog */}
      <ModalPortal isOpen={importDialogOpen}>
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          dataType={dataType}
          onImport={() => {
            setImportDialogOpen(false);
            fetchItems();
          }}
        />
      </ModalPortal>
    </div>
  );
};

// Dialog for creating/editing items
const ItemDialog = ({ open, onClose, dataType, editingItem, onSave }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const schemaFields = Object.entries(dataType.schema || {});

  useEffect(() => {
    if (editingItem) {
      setValues({ ...editingItem });
    } else {
      const initial = {};
      schemaFields.forEach(([field, config]) => {
        initial[field] = config.type === "string[]" ? [] : "";
      });
      setValues(initial);
    }
    setError("");
  }, [editingItem, open]);

  const handleChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setError("");
    
    // Validate required fields
    for (const [field, config] of schemaFields) {
      if (config.required && !values[field]) {
        setError(`${field} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      const data = { ...values };
      delete data.id;

      if (editingItem) {
        await updateDoc(doc(db, "referenceData", dataType.id, "items", editingItem.id), data);
      } else {
        await addDoc(collection(db, "referenceData", dataType.id, "items"), data);
      }
      // Update parent updatedAt
      await updateDoc(doc(db, "referenceData", dataType.id), { updatedAt: serverTimestamp() });
      onSave();
    } catch (error) {
      console.error("Error saving item:", error);
      setError("Failed to save");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>{editingItem ? "Edit Item" : "Add Item"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {schemaFields.map(([field, config]) => (
            <TextField
              key={field}
              label={field + (config.required ? " *" : "")}
              value={
                config.type === "string[]" 
                  ? (values[field] || []).join(", ")
                  : values[field] || ""
              }
              onChange={(e) => {
                const val = config.type === "string[]"
                  ? e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                  : config.type === "number"
                  ? Number(e.target.value)
                  : e.target.value;
                handleChange(field, val);
              }}
              fullWidth
              multiline={config.type === "string" && field.toLowerCase().includes("description")}
              rows={config.type === "string" && field.toLowerCase().includes("description") ? 4 : 1}
              type={config.type === "number" ? "number" : "text"}
              helperText={config.type === "string[]" ? "Comma-separated values" : ""}
            />
          ))}
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog for bulk JSON import
const ImportDialog = ({ open, onClose, dataType, onImport }) => {
  const [jsonText, setJsonText] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setJsonText("");
    setError("");
    setPreview(null);
  }, [open]);

  const handlePreview = () => {
    setError("");
    try {
      const data = JSON.parse(jsonText);
      if (!Array.isArray(data)) {
        setError("JSON must be an array of objects");
        return;
      }
      setPreview(data);
    } catch (e) {
      setError("Invalid JSON");
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const batch = preview.map(item => 
        addDoc(collection(db, "referenceData", dataType.id, "items"), item)
      );
      await Promise.all(batch);
      await updateDoc(doc(db, "referenceData", dataType.id), { updatedAt: serverTimestamp() });
      onImport();
    } catch (error) {
      console.error("Error importing:", error);
      setError("Import failed");
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>Import JSON</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="JSON Array"
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setPreview(null); }}
            fullWidth
            multiline
            rows={10}
            placeholder='[{"field1": "value1", "field2": "value2"}, ...]'
            sx={{ fontFamily: 'monospace' }}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
          {preview && (
            <Box sx={{ p: 2, bgcolor: 'var(--color-background-alt)', borderRadius: 1 }}>
              <strong>{preview.length} items ready to import</strong>
              <Box sx={{ mt: 1, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                Fields: {Object.keys(preview[0] || {}).join(", ")}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!preview ? (
          <Button onClick={handlePreview} variant="outlined">
            Preview
          </Button>
        ) : (
          <Button onClick={handleImport} variant="contained" disabled={importing}>
            {importing ? "Importing..." : `Import ${preview.length} Items`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdminReferenceDataEditor;
